import { useMemo } from "react";
import { IconMapPin, IconUser } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { SectionCard } from "../../../../components/common/section-card";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { usePartnerChildren } from "../../hooks/usePartnerChildren";
import { type PartnerChildDto, PartnerChildDtoTypeEnum } from "../../../../api";

interface PartnerContactsAddressesTabProps {
  partnerId: number;
}

const ADDRESS_TYPE_LABEL: Record<string, string> = {
  [PartnerChildDtoTypeEnum.Delivery]: "Delivery",
  [PartnerChildDtoTypeEnum.Invoice]: "Invoice",
  [PartnerChildDtoTypeEnum.Other]: "Other",
  [PartnerChildDtoTypeEnum.Private]: "Private",
};

function formatAddressLine(row: PartnerChildDto): string {
  const parts = [row.street, row.street2, row.city, row.zip, row.country?.name].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length > 0 ? parts.join(", ") : "—";
}

export function PartnerContactsAddressesTab({ partnerId }: PartnerContactsAddressesTabProps) {
  const { data: children, isLoading } = usePartnerChildren(partnerId);

  const { contacts, addresses } = useMemo(() => {
    const rows = children ?? [];
    return {
      contacts: rows.filter((r) => r.type === PartnerChildDtoTypeEnum.Contact),
      addresses: rows.filter((r) => r.type !== PartnerChildDtoTypeEnum.Contact),
    };
  }, [children]);

  const contactColumns = useMemo((): SimpleTableColumn<PartnerChildDto>[] => [
    {
      key: "name",
      label: "Name",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-0">
          <IconUser size={18} className="text-textMuted shrink-0" />
          <Text variant="bodyBold" tone="dark">{row.name || "—"}</Text>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      className: "flex-[2] min-w-0",
      render: (row) => <Text variant="body" tone="body">{row.email || "—"}</Text>,
    },
    {
      key: "phone",
      label: "Phone",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{row.phone || "—"}</Text>,
    },
  ], []);

  const addressColumns = useMemo((): SimpleTableColumn<PartnerChildDto>[] => [
    {
      key: "name",
      label: "Label",
      className: "flex-[2] min-w-0",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-0">
          <IconMapPin size={18} className="text-textMuted shrink-0" />
          <Text variant="bodyBold" tone="dark">{row.name || "—"}</Text>
        </div>
      ),
    },
    {
      key: "type",
      label: "Type",
      className: "flex-1",
      render: (row) => (
        <Chip variant="secondary">{ADDRESS_TYPE_LABEL[row.type] ?? row.type}</Chip>
      ),
    },
    {
      key: "address",
      label: "Address",
      className: "flex-[3] min-w-0",
      render: (row) => <Text variant="body" tone="body">{formatAddressLine(row)}</Text>,
    },
    {
      key: "phone",
      label: "Phone",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{row.phone || "—"}</Text>,
    },
  ], []);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <SectionCard title="Contacts">
          <ShimmerLoader message="Fetching contacts and addresses…">
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Shimmer key={i} className="h-12 w-full" />
              ))}
            </div>
          </ShimmerLoader>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={`Contacts (${contacts.length})`}>
        {contacts.length === 0 ? (
          <Text variant="body" tone="muted">No contacts on file.</Text>
        ) : (
          <SimpleTable
            columns={contactColumns}
            data={contacts}
            keyExtractor={(row) => String(row.id)}
          />
        )}
      </SectionCard>

      <SectionCard title={`Addresses (${addresses.length})`}>
        {addresses.length === 0 ? (
          <Text variant="body" tone="muted">No saved addresses.</Text>
        ) : (
          <SimpleTable
            columns={addressColumns}
            data={addresses}
            keyExtractor={(row) => String(row.id)}
          />
        )}
      </SectionCard>
    </div>
  );
}
